import { Box, BoxProps, Heading, Spinner, Text } from '@razorpay/blade/components';
import React from 'react';
import styled from 'styled-components';

/**
 * Usage: eg.
 * ```
 * <Modal>
 *   <ModalBody>
 *     <MultiStepFormWrapper>
 *        <SidebarContentWrapper>{children}</SidebarContentWrapper>
 *        <MainContentWrapper>
 *          <StyledForm>
 *            <FormFieldsWrapper>
 *              <FormHeading title="Basic Details" description="Add basic details for the employee" />
 *              Rest All Input fields
 *            </FormFieldsWrapper>
 *            <FormFooterWrapper>
 *              All Buttons
 *            </FormFooterWrapper>
 *          </StyledForm>
 *        </MainContentWrapper>
 *     </MultiStepFormWrapper>
 *   </ModalBody>
 * </Modal>
 */
export const MultiStepFormWrapper = ({
  children,
  ...boxProps
}: React.PropsWithChildren<BoxProps>) => {
  return (
    <Box display="flex" height="calc(100vh - 16px)" {...boxProps}>
      {children}
    </Box>
  );
};

export const SidebarContentWrapper = ({
  children,
  ...boxProps
}: React.PropsWithChildren<BoxProps>) => {
  return (
    <Box
      position="fixed"
      width={{ base: '240px', m: '284px' }}
      height="calc(100vh - 16px)"
      backgroundColor="surface.background.gray.moderate"
      paddingX="spacing.8"
      paddingTop="spacing.9"
      paddingBottom="spacing.8"
      {...boxProps}>
      {children}
    </Box>
  );
};

export const MainContentWrapper = ({
  children,
  ...boxProps
}: React.PropsWithChildren<BoxProps>) => {
  return (
    <Box
      flex={1}
      backgroundColor="surface.background.gray.intense"
      display="flex"
      flexDirection="column"
      height="100%"
      marginLeft={{ base: '240px', m: '284px' }}
      {...boxProps}>
      {children}
    </Box>
  );
};

export const StyledForm = styled.form`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

export const BorderedBox = ({ children, ...boxProps }: React.PropsWithChildren<BoxProps>) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="spacing.7"
      padding="spacing.7"
      borderColor="surface.border.gray.muted"
      borderWidth="thinner"
      borderRadius="large"
      {...boxProps}>
      {children}
    </Box>
  );
};

export const FormFieldsWrapper = ({
  children,
  shouldShowSpinner = false,
  ...boxProps
}: React.PropsWithChildren<
  BoxProps & {
    shouldShowSpinner?: boolean;
  }
>) => (
  <Box overflow="auto" flexGrow={1}>
    <Box
      display="flex"
      flexDirection="column"
      gap="spacing.7"
      maxWidth="584px"
      marginX="auto"
      marginY="spacing.11"
      {...boxProps}>
      {shouldShowSpinner ? <Spinner accessibilityLabel="Loading" /> : children}
    </Box>
  </Box>
);

export const FormHeading = ({
  title,
  description,
  ...boxProps
}: { title: string; description: string } & BoxProps) => {
  return (
    <Box {...boxProps}>
      <Heading size="large" weight="regular" color="surface.text.gray.normal">
        {title}
      </Heading>
      <Text size="medium" weight="regular" color="surface.text.gray.muted" marginTop="spacing.2">
        {description}
      </Text>
    </Box>
  );
};

export const FormFooterWrapper = ({ children, ...boxProps }: React.PropsWithChildren<BoxProps>) => (
  <Box
    backgroundColor="surface.background.gray.intense"
    borderTopColor="surface.border.gray.muted"
    borderTopWidth="thin">
    <Box
      display="flex"
      margin="auto"
      justifyContent="space-between"
      paddingY="spacing.4"
      maxWidth="584px"
      {...boxProps}>
      {children}
    </Box>
  </Box>
);
